/**
 * Get authentication token from storage
 * Only uses sessionStorage to avoid stale tokens from localStorage
 */
export const getAuthToken = (): string | null => {
  return sessionStorage.getItem('token');
};

/**
 * Get user data from storage and clean it
 * Only uses sessionStorage (no localStorage fallback) to ensure the
 * currently logged-in user's data is shown
 */
export const getUserData = (): string | null => {
  const userString = sessionStorage.getItem('user');

  if (!userString) return null;

  try {
    // Parse and clean the user data to remove duplicate 'role' field
    const userData = JSON.parse(userString);
    const { role, ...cleanUserData } = userData;
    return JSON.stringify(cleanUserData);
  } catch (error) {
    return userString; // Return as-is if parsing fails
  }
};

/**
 * Get tenant schema from storage
 * Checks sessionStorage first (new behavior), then localStorage (fallback)
 */
export const getTenantSchema = (): string | null => {
  return sessionStorage.getItem('tenantSchema') || localStorage.getItem('tenantSchema');
};

/**
 * Store authentication token
 * Uses sessionStorage (clears when tab closes)
 */
export const setAuthToken = (token: string): void => {
  sessionStorage.setItem('token', token);
  // Clear from localStorage if it exists there
  localStorage.removeItem('token');
};

/**
 * Store user data and clean it
 * Uses sessionStorage (clears when tab closes)
 * Removes duplicate 'role' field before storing
 */
export const setUserData = (userData: string): void => {
  try {
    // Parse and clean the user data to remove duplicate 'role' field
    const parsedData = JSON.parse(userData);
    const { role, ...cleanUserData } = parsedData;
    sessionStorage.setItem('user', JSON.stringify(cleanUserData));
  } catch (error) {
    // If parsing fails, store as-is
    sessionStorage.setItem('user', userData);
  }
  // Clear from localStorage if it exists there
  localStorage.removeItem('user');
};

/**
 * Store tenant schema
 * Uses sessionStorage (clears when tab closes)
 */
export const setTenantSchema = (schema: string): void => {
  sessionStorage.setItem('tenantSchema', schema);
  // Clear from localStorage if it exists there
  localStorage.removeItem('tenantSchema');
};

/**
 * Clear all authentication data from both storages
 */
export const clearAuthData = (): void => {
  // Clear from sessionStorage
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('tenantSchema');
  sessionStorage.removeItem('studentData');
  sessionStorage.removeItem('selectedStudentCode');

  // Also clear from localStorage for cleanup
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('tenantSchema');
  localStorage.removeItem('studentClassId'); // Clear stored student class ID
  localStorage.removeItem('studentSessionId'); // Clear stored student session ID

  // Clear school form draft data on logout
  clearSchoolFormDraft();
  clearApplicationFormDraft();
};

/**
 * Save school form draft data to localStorage
 */
export const saveSchoolFormDraft = (formData: any): void => {
  try {
    localStorage.setItem('schoolFormDraft', JSON.stringify(formData));
  } catch (error) {
  }
};

/**
 * Get school form draft data from localStorage
 */
export const getSchoolFormDraft = (): any | null => {
  try {
    const draft = localStorage.getItem('schoolFormDraft');
    return draft ? JSON.parse(draft) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Clear school form draft data from localStorage
 */
export const clearSchoolFormDraft = (): void => {
  localStorage.removeItem('schoolFormDraft');
};

/**
 * Save application form draft data to localStorage
 */
export const saveApplicationFormDraft = (formData: any): void => {
  try {
    localStorage.setItem('applicationFormDraft', JSON.stringify(formData));
  } catch (error) {
  }
};

/**
 * Get application form draft data from localStorage
 */
export const getApplicationFormDraft = (): any | null => {
  try {
    const draft = localStorage.getItem('applicationFormDraft');
    return draft ? JSON.parse(draft) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Clear application form draft data from localStorage
 */
export const clearApplicationFormDraft = (): void => {
  localStorage.removeItem('applicationFormDraft');
};

/**
 * Save student data to storage
 */
export const setStudentData = (studentData: any): void => {
  try {
    sessionStorage.setItem('studentData', JSON.stringify(studentData));
  } catch (error) {
  }
};

/**
 * Get student data from storage
 */
export const getStudentData = (): any | null => {
  try {
    const studentString = sessionStorage.getItem('studentData');
    return studentString ? JSON.parse(studentString) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Clear student data from storage
 */
export const clearStudentData = (): void => {
  sessionStorage.removeItem('studentData');
};

export const setSchoolData = (schoolData: any): void => {
  try {
    localStorage.setItem('schoolData', JSON.stringify(schoolData));
  } catch (error) {
    // Handle storage errors silently
  }
};

export const getSchoolData = (): any | null => {
  try {
    const schoolString = localStorage.getItem('schoolData');
    return schoolString ? JSON.parse(schoolString) : null;
  } catch (error) {
    return null;
  }
};

export const clearSchoolData = (): void => {
  localStorage.removeItem('schoolData');
};

/**
 * Save selected student code to sessionStorage
 */
export const setSelectedStudentCode = (code: string): void => {
  sessionStorage.setItem('selectedStudentCode', code);
};

/**
 * Get selected student code from sessionStorage
 */
export const getSelectedStudentCode = (): string | null => {
  return sessionStorage.getItem('selectedStudentCode');
};

/**
 * Clear selected student code from sessionStorage
 */
export const clearSelectedStudentCode = (): void => {
  sessionStorage.removeItem('selectedStudentCode');
};